import ArrowLeftIcon from '@ping/assets/Icon/arrowLeft.svg';
import CloseIcon from '@ping/assets/Icon/close.svg';
import { disablePageScroll } from '@ping/utils';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import ReactModal, { Props as ReactModalProps } from 'react-modal';

import style from './style.module.scss';

export interface IPopupProps extends Omit<ReactModalProps, 'children'> {
  isOpen: boolean;
  title?: React.ReactNode;
  isHeaderVisible?: boolean;
  onClose?: () => void;
  onBack?: () => void;
  fullscreen?: boolean;
  children: React.ReactNode;
  contentClassName?: string;
  className?: string;
  uniqueId?: string;
  shouldSlide?: boolean;
  closeIcon?: boolean;
  inlineSize?: 'narrow' | 'wide' | 'wider' | 'widest' | 'full';
}

const ModalComponent = ReactModal as unknown as React.FC<ReactModalProps>;

export function Popup({
  title,
  isOpen,
  isHeaderVisible = true,
  onClose,
  onBack,
  children,
  fullscreen,
  className,
  contentClassName,
  uniqueId,
  shouldSlide = false,
  closeIcon = true,
  inlineSize = 'wide',
  ...rest
}: IPopupProps) {
  /* handling animation state */
  const [closing, setClosing] = useState<boolean>(false);
  const modalIdentifier = useRef(Math.random().toString(16));

  const closeAnimationMs = shouldSlide ? 200 : 100;

  const handleOnClose = () => {
    setClosing(true);
    if (onClose) {
      setTimeout(onClose, closeAnimationMs);
    }
  };
  const handleOnBack = () => {
    setClosing(true);
    if (onBack) {
      setTimeout(onBack, closeAnimationMs);
    }
  };

  useEffect(() => {
    disablePageScroll(isOpen, modalIdentifier.current);

    /* reset closing animation when is open trigger  again */
    isOpen && setClosing(false);
  }, [isOpen]);

  const contentClassNames = clsx(style['popup__content'], contentClassName, {
    [style['popup__closing']]: closing && !shouldSlide,
    'popup-modal-drawer-content--closing': closing && shouldSlide,
  });
  const headerClassName = clsx(style['popup__header'], {
    [style['center']]: Boolean(onBack),
  });

  return (
    <ModalComponent
      isOpen={isOpen}
      onRequestClose={handleOnClose}
      className={clsx(style['popup'], {
        [style['fullScreen']]: fullscreen,
        [`${className}`]: className,
        'popup-modal-drawer': shouldSlide,
      })}
      overlayClassName={style['popup__overlay']}
      closeTimeoutMS={closeAnimationMs}
      ariaHideApp={false}
      {...rest}
    >
      <div
        className={clsx({ 'popup-modal-drawer-content': shouldSlide }, contentClassNames)}
        data-popup={uniqueId}
        data-inline-size={inlineSize}
      >
        {isHeaderVisible && (
          <div className={headerClassName}>
            {Boolean(onBack) && <ArrowLeftIcon className={style['popup__back']} onClick={handleOnBack} />}
            <div className={style['popup__left-part']}>
              {title && <h4 className={style['popup__left-part-title']}>{title}</h4>}
            </div>
            {Boolean(onClose) && closeIcon && <CloseIcon className='popup_close-btn' onClick={handleOnClose} />}
          </div>
        )}

        {children}
      </div>
    </ModalComponent>
  );
}
